import React, { useCallback, useEffect, useState } from 'react';
import { fetchShipper, updateShipper } from 'modules/shippers/http';
import { openFinishedWithFilter } from 'redux/modules/finished-journeys/effects';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Flexer, Icon, Link, Text, Tooltip } from '@ontruck/orc';
import IBANModule from 'iban';
import { CopyableLink } from 'modules/Common/Copy';
import ShipperDetailUsers from './shipper-detail-users';
import PropTypes from 'prop-types';
import { Loading } from 'components/Loading';
import ShipperUserForm from 'modules/shippers/shipper-user-form';
import cx from 'classnames';
import { shipperUsersIncreasingAlphabetical as alphabetical } from 'utils/sort';
import ShipperEditModal from 'components/ShipperEditModal';
import { Route, useRouteMatch, useParams, useHistory } from 'react-router-dom';
import { ReactComponent as EyeOffIcon } from 'assets/icons/eye-off.svg';
import { injectIntl } from 'react-intl';
import { constants, getSpecificClientConfig } from 'redux/modules/client-config/selectors';

const ShipperDetail = ({ className, intl }) => {
  const t = intl.messages.modules.shippers['shipper-detail'];
  const shippersMinimalisticForms = useSelector((state) =>
    getSpecificClientConfig(state, constants.SHIPPERS_MINIMALISTIC_FORMS)
  );

  const [loading, setLoading] = useState(false);
  const [shipper, setShipper] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(null);
  const [editedUserId, setEditedUserId] = useState(null);
  const [editShipperModalOpen, setEditshipperModalOpen] = useState(false);
  const { shipperId } = useParams();
  const dispatch = useDispatch();
  const { url } = useRouteMatch();
  const history = useHistory();

  const { owner_username } = shipper?.sales_attributes || {};

  const getShipper = useCallback(async () => {
    setLoading(true);
    try {
      const shipper = await fetchShipper(shipperId);

      if (shipper) setShipper(shipper);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [shipperId]);

  // ACTION HANDLERS
  const onEditShipper = () => {
    setEditshipperModalOpen(true);
  };

  const onAddUser = () => {
    setSidebarOpen(true);
    setEditedUserId(null);
    history.push({
      pathname: `/shipper/${shipper?.id}/create-user`,
    });
  };

  const onEditUser = (user) => {
    setSidebarOpen(true);
    history.push({
      pathname: `/shipper/${shipper?.id}/user/${user?.id}`,
    });
  };

  const onUserModified = useCallback(
    (modifiedUser) => {
      const index = shipper?.users?.findIndex(({ id }) => id === modifiedUser.id);
      if (index >= 0) {
        const shipperUpdated = { ...shipper };
        shipperUpdated.users[index] = modifiedUser;

        setShipper(shipperUpdated);
      }
    },
    [shipper]
  );

  const onUserAdded = (newUser) => {
    setShipper((currentShipper) => ({
      ...currentShipper,
      users: [...currentShipper?.users, newUser].sort(alphabetical),
    }));
  };

  const onShipperModified = (modifiedShipper) => {
    setShipper((currentShipper) => ({ ...modifiedShipper, users: currentShipper.users }));
  };

  useEffect(() => {
    getShipper();
  }, [getShipper]);

  if (loading) {
    return (
      <Flexer.Parent justifyContent="center" mTop="L">
        <Loading />
      </Flexer.Parent>
    );
  }

  if (!shipper) return null;

  return (
    <div className={className} data-testid="shipper-detail">
      <div className={cx('shipper-detail')}>
        <div className="shipper-detail__header">
          <div className="shipper-detail__header-content">
            <strong>{shipper.company_name}</strong>
          </div>
        </div>
        <div
          className={cx('shipper-detail__body', {
            'sidebar-open': sidebarOpen,
            'sidebar-closed': !sidebarOpen,
          })}
        >
          {/* Shipper information */}
          <Flexer.Parent alignItems="center">
            <Flexer.Child flexGrow={1}>
              <Flexer.Parent alignItems="center">
                <h2 className="shipper-detail__title">{t.shipper_information}</h2>
                <Link
                  data-testid="shipper-detail__finished-with-filter"
                  onClick={() => dispatch(openFinishedWithFilter({ shipper }))}
                  text={`${shipper.shipments} ${t.orders_completed}`}
                />
                {shipper.is_hidden_on_search && (
                  <>
                    <EyeOffIcon className="shipper-detail__hidden-icon" />
                    <Text color="grey900" text={t.hidden_on_search} />
                  </>
                )}
              </Flexer.Parent>
            </Flexer.Child>

            <Button ghost skin="primary" onClick={onEditShipper}>
              {t.edit_shipper}
            </Button>
          </Flexer.Parent>

          <Flexer.Parent className="shipper-detail__container">
            {/* Fiscal address */}
            <Flexer.Child flexGrow={1}>
              <h3 className="shipper-detail__subtitle">{t.fiscal_address}</h3>
              {shipper.fiscal_address ? (
                <>
                  <p>{shipper.fiscal_address.address_line}</p>
                  <p>{shipper.fiscal_address.zipcode}</p>
                  <p>{shipper.fiscal_address.city}</p>
                </>
              ) : (
                '-'
              )}
            </Flexer.Child>

            {/* Payment details */}
            <Flexer.Child flexGrow={1}>
              <h3 className="shipper-detail__subtitle">{t.payment_details}</h3>
              <p>{shipper.tax_number}</p>
              {shipper.payment_method ? (
                <p>
                  {`${t.payment_type}: ${
                    t.payment_types[shipper.payment_method] || shipper.payment_method
                  }`}
                </p>
              ) : (
                <p>{t.no_payment_method}</p>
              )}
              {shipper.bank_account ? (
                <p>{IBANModule.printFormat(shipper.bank_account, ' ')}</p>
              ) : null}
            </Flexer.Child>

            {/* Ontruck relations */}
            {!shippersMinimalisticForms && (
              <Flexer.Child flexGrow={1}>
                <h3 className="shipper-detail__subtitle">{t.ontruck_relations}</h3>
                {!!owner_username && (
                  <p>
                    {`${t.account_manager}: `}
                    <b>
                      <CopyableLink text={owner_username} />
                    </b>
                  </p>
                )}
                {!!shipper.special_requirements && (
                  <Link
                    href={shipper.special_requirements}
                    target="_blank"
                    text={t.special_requeriments}
                  />
                )}
              </Flexer.Child>
            )}
          </Flexer.Parent>

          {/* Users */}
          <Flexer.Parent alignItems="center" mTop="M">
            <Flexer.Child flexGrow={1}>
              <Flexer.Parent alignItems="center">
                <h2 className="shipper-detail__title">{`${t.users} (${
                  shipper.users?.length || 0
                })`}</h2>
                <Tooltip component={t.users_info} skin="light" position="right">
                  <Icon glyph="info" name="info" size={'1.4rem'} color="primary300" />
                </Tooltip>
              </Flexer.Parent>
            </Flexer.Child>

            <Button ghost skin="primary" onClick={onAddUser}>
              {t.add_user}
            </Button>
          </Flexer.Parent>

          <ShipperDetailUsers
            shipperUsers={shipper.users}
            onEditUser={onEditUser}
            onUserModified={onUserModified}
            openId={editedUserId}
          />
        </div>
      </div>
      <Route
        path={`${url}/create-user`}
        component={() => (
          <ShipperUserForm shipperId={shipper?.id} mode="CREATE" onUserAdded={onUserAdded} />
        )}
      />
      <Route
        path={`${url}/user/:userId`}
        component={() => (
          <ShipperUserForm
            shipperId={shipper?.id}
            mode="EDIT"
            onUserModified={onUserModified}
            onUserOpened={setEditedUserId}
          />
        )}
      />

      {editShipperModalOpen && (
        <ShipperEditModal
          show={editShipperModalOpen}
          onClose={() => setEditshipperModalOpen(false)}
          shipper={shipper}
          onSave={updateShipper}
          onSaveSuccesfully={(shipperUpdated) => {
            onShipperModified(shipperUpdated);
            setEditshipperModalOpen(false);
          }}
          shippersMinimalisticForms={shippersMinimalisticForms}
        />
      )}
    </div>
  );
};

ShipperDetail.propTypes = {
  className: PropTypes.string,
};

export default injectIntl(ShipperDetail);
